import { useState, useEffect, useRef, useMemo } from "react"
import Select from "react-select"
import { getCurrencySymbol } from '../utils/getCurrencySymbol';
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { showSuccessToast, showErrorToast } from "../components/Toast";
import { useDispatch, useSelector } from "react-redux"
import { fetchAssets, deleteAsset } from "../redux/assetSlice"
import { api, getAuthHeaders } from "../utils/api";
import Layout from '../components/Layout';
import * as XLSX from "xlsx";
import "../styles/my-assets-liability.scss"
import Loader from "../components/Loader";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { formatLargeNumber } from '../utils/formatNumber';
import CountrySelectReact from '../utils/CountrySelect';
import { getDialCodeFromUserProfile } from '../utils/localeDetection';
import TruncatedTooltipText from "../components/TruncatedTooltipText";

const MetricValue = ({ text }) => <TruncatedTooltipText text={text} />;

const formatAssetType = (type) => {
  if (!type) return '';
  const result = type.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Card title: the label the user typed (e.g. "Emergency Fund FD") with the institution, else the asset name (ML-39).
const assetCardTitle = (asset) => {
  const label = asset?.fixedDepositDetails?.depositLabel || asset?.cashDetails?.cashLabel;
  if (label && asset?.name && label !== asset.name) return `${label} · ${asset.name}`;
  return label || asset?.name || "";
};

const formatArchiveReason = (reason) => {
  if (!reason) return 'Plan Downgrade';
  return reason
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const MyAssets = () => {
  const [performanceAnalytics, setPerformanceAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/assets/analytics/performance', { headers: getAuthHeaders() });
        setPerformanceAnalytics(response.data);
      } catch (e) {
        console.error('Failed to fetch performance analytics:', e);
      }
    })();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [isValueVisible, setIsValueVisible] = useState(true)
  const [viewMode, setViewMode] = useState("grid")
  const [page, setPage] = useState(1);

  // Archived assets state
  const [archivedAssets, setArchivedAssets] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState(null);

  // Plan limit state
  const [planInfo, setPlanInfo] = useState({ activeLimit: -1, activeCount: 0 });

  const fetchPlanInfo = async () => {
    try {
      const res = await api.get('/subscriptions/my', { headers: getAuthHeaders() });
      const sub = res.data?.subscription || res.data?.plan || res.data || {};
      const limit = sub.planId?.limits?.digitalAssets ?? sub.limits?.digitalAssets ?? -1;
      const used = sub.usageTracking?.digitalAssets?.current ?? sub.usageTracking?.digitalAssets?.used ?? 0;
      setPlanInfo({ activeLimit: limit, activeCount: used });
    } catch (e) {
      // non-blocking — keep defaults
    }
  };

  const fetchArchivedAssets = async () => {
    setArchivedLoading(true);
    setArchivedError(null);
    try {
      const response = await api.get('/assets/archived', { headers: getAuthHeaders() });
      const data = response.data?.results || response.data || [];
      setArchivedAssets(Array.isArray(data) ? data : []);
    } catch (e) {
      setArchivedError(e?.response?.data?.message || e?.message || 'Failed to fetch archived assets.');
    } finally {
      setArchivedLoading(false);
    }
  };

  // Auto-switch to archived tab when redirected from downgrade flow
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'archived') {
      setActiveFilter('archived');
      fetchArchivedAssets();
    }
  }, [location.search]);

  // Fetch plan info on mount
  useEffect(() => { fetchPlanInfo(); }, []);

  const handleArchive = async (assetId) => {
    try {
      await api.post(`/assets/${assetId}/archive`, {}, { headers: getAuthHeaders() });
      showSuccessToast('Asset archived successfully!');
      dispatch(fetchAssets({ page, limit: 10, sortBy: 'createdAt:desc' }));
      if (activeFilter === 'archived') fetchArchivedAssets();
      fetchPlanInfo();
    } catch (e) {
      showErrorToast(e?.response?.data?.message || 'Failed to archive asset.');
    }
  };

  const handleReactivate = async (assetId) => {
    try {
      await api.post(`/assets/${assetId}/reactivate`, {}, { headers: getAuthHeaders() });
      showSuccessToast('Asset reactivated successfully!');
      setArchivedAssets((prev) => prev.filter((a) => (a.id || a._id) !== assetId));
      dispatch(fetchAssets({ page, limit: 10, sortBy: 'createdAt:desc' }));
      fetchPlanInfo();
    } catch (e) {
      if (e?.response?.status === 403) {
        showErrorToast("You've reached your plan limit. Please archive a digital asset or upgrade your plan.");
      } else {
        showErrorToast(e?.response?.data?.message || 'Failed to reactivate asset.');
      }
    }
  };

  const [bulkReactivating, setBulkReactivating] = useState(false);

  const handleBulkReactivate = async () => {
    setBulkReactivating(true);
    try {
      const assetIds = archivedAssets.map((a) => a.id || a._id).filter(Boolean);
      if (assetIds.length === 0) {
        showErrorToast('No archived assets to reactivate.');
        return;
      }
      await api.post('/assets/bulk-reactivate', { assetIds }, { headers: getAuthHeaders() });
      showSuccessToast('All archived assets reactivated successfully!');
      await fetchArchivedAssets();
      dispatch(fetchAssets({ page, limit: 10, sortBy: 'createdAt:desc' }));
      fetchPlanInfo();
    } catch (e) {
      if (e?.response?.status === 403) {
        showErrorToast("You've reached your plan limit. Upgrade your plan to reactivate all assets.");
      } else {
        showErrorToast(e?.response?.data?.message || 'Failed to bulk reactivate assets.');
      }
    } finally {
      setBulkReactivating(false);
    }
  };

  // reactivateDisabled only applies to digital assets — physical assets are unlimited on all plans
  const reactivateDisabled = planInfo.activeLimit !== -1 && planInfo.activeCount >= planInfo.activeLimit;
  const isReactivateDisabled = (asset) => {
    const category = (asset?.assetCategory || '').toLowerCase();
    if (category === 'physical') return false;
    return reactivateDisabled;
  };

  const [isShareMode, setIsShareMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [showAddCollaboratorModal, setShowAddCollaboratorModal] = useState(false);
  const [showCollaboratorAddedModal, setShowCollaboratorAddedModal] = useState(false);
  const [showAssetsSharedModal, setShowAssetsSharedModal] = useState(false);
  const [sharedByMeResponse, setSharedByMeResponse] = useState(null);
  const [alreadySharedAssetIds, setAlreadySharedAssetIds] = useState([]);
  const [newCollaboratorName, setNewCollaboratorName] = useState('');
  const [sharedAssetsCount, setSharedAssetsCount] = useState(0);

  const dispatch = useDispatch()
  const { list: assets, listLoading: loading, listError: error } = useSelector((state) => state.assets)

  const searchTimeout = useRef();
  const [categoryLoading, setCategoryLoading] = useState(false);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchTerm && searchTerm.trim().length >= 3) {
      searchTimeout.current = setTimeout(async () => {
        try {
          const url = `/assets?search=${encodeURIComponent(searchTerm)}&page=${page}&limit=10&sortBy=createdAt:desc`;
          const response = await api.get(url, { headers: getAuthHeaders() });
          dispatch({ type: 'assets/fetchAssets/fulfilled', payload: response.data });
        } catch (e) {
          dispatch({ type: 'assets/fetchAssets/rejected', error: { message: e?.message || 'Search failed' } });
        }
      }, 500);
    } else if (!searchTerm || searchTerm.trim().length === 0) {
      if (activeFilter === 'physical' || activeFilter === 'digital') {
        const categoryType = activeFilter === 'physical' ? 'physical' : 'digital';
        (async () => {
          setCategoryLoading(true);
          try {
            const response = await api.get(
              `/assets?assetCategory=${categoryType}&page=${page}&limit=10&sortBy=createdAt:desc`,
              { headers: getAuthHeaders() }
            );
            let payload = Array.isArray(response.data)
              ? { results: response.data }
              : response.data;
            dispatch({ type: 'assets/fetchAssets/fulfilled', payload });
          } catch (e) {
            dispatch({ type: 'assets/fetchAssets/rejected', error: { message: e?.message || 'Category fetch failed' } });
          } finally {
            setCategoryLoading(false);
          }
        })();
      } else {
        dispatch(fetchAssets({ page, limit: 10, sortBy: "createdAt:desc" }));
      }
    }
    return () => clearTimeout(searchTimeout.current);
  }, [dispatch, page, searchTerm, activeFilter]);

  const totalPages = useSelector((state) => state.assets.listMeta?.totalPages) || 1;
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const filteredAssets = assets && assets.length > 0 ? assets.filter((asset) => {
    const matchesFilter =
      activeFilter === "all" ||
      (activeFilter === "physical" && (asset.assetCategory === "physical" || asset.assetCategory === "Physical")) ||
      (activeFilter === "digital" && (asset.assetCategory === "digital" || asset.assetCategory === "Digital"));

    const name = (asset.name || "").toLowerCase();
    const type = (asset.assetType || "").toLowerCase();
    const matchesSearch =
      name.includes((searchTerm || "").toLowerCase()) ||
      type.includes((searchTerm || "").toLowerCase());

    return matchesFilter && matchesSearch;
  }) : [];

  const toggleFavorite = (id) => {
  }

  const duplicateAsset = async (id) => {
    try {
      const response = await api.post(`/assets/${id}/clone`, {}, { headers: getAuthHeaders() });
      if (response && response.data) {
        showSuccessToast('Asset duplicated successfully!');
        await dispatch(fetchAssets({ page: 1, limit: 10, sortBy: "createdAt:desc" }));
      } else {
        showErrorToast('Failed to duplicate asset.');
      }
    } catch (e) {
      // Extract error message from API response
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to duplicate asset.';
      showErrorToast(errorMessage);
    }
  }

  const [deleteModal, setDeleteModal] = useState({ show: false, assetId: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const handleDeleteClick = (assetId) => {
    setDeleteModal({ show: true, assetId });
  };
  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, assetId: null });
  };
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const result = await dispatch(deleteAsset(deleteModal.assetId));
      if (deleteAsset.fulfilled.match(result)) {
        showSuccessToast('Asset deleted successfully!');
        setDeleteModal({ show: false, assetId: null });
        await dispatch(fetchAssets({ page: 1, limit: 10, sortBy: "createdAt:desc" }));
        
        if (activeFilter === 'archived') {
          await fetchArchivedAssets();
        }
        await fetchPlanInfo();

        // Refresh performance analytics after delete
        try {
          const response = await api.get('/assets/analytics/performance', { headers: getAuthHeaders() });
          setPerformanceAnalytics(response.data);
        } catch (e) {
          console.error('Failed to fetch performance analytics:', e);
        }
      } else {
        showErrorToast(result?.error?.message || 'Failed to delete asset.');
      }
    } catch (e) {
      showErrorToast(e?.message || 'Failed to delete asset.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await api.get('/assets?page=1&limit=1000&sortBy=createdAt:desc', { headers: getAuthHeaders() });
      const assetsData = response.data?.results || response.data || [];
      if (!Array.isArray(assetsData) || assetsData.length === 0) {
        showErrorToast('No asset data to export.');
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(assetsData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Assets");
      XLSX.writeFile(workbook, "assets_export.xlsx");
      showSuccessToast('Exported all assets to Excel!');
    } catch (e) {
      showErrorToast(e?.message || 'Failed to export assets.');
    }
  }

  useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        const response = await api.get('/sharing/collaborators?status=active&limit=20&page=1', { headers: getAuthHeaders() });
        // Map response to expected format for dropdown
        let collabList = Array.isArray(response.data?.results)
          ? response.data.results.map(item => ({
              id: item.collaboratorId?.id,
              name: item.collaboratorId?.name || "",
              email: item.collaboratorId?.email || "",
              mobileNumber: item.collaboratorId?.mobileNumber || "",
              firstName: item.collaboratorId?.firstName || "",
              lastName: item.collaboratorId?.lastName || ""
            }))
          : [];
        setCollaborators(collabList);
      } catch (e) {
        console.error('Failed to fetch collaborators:', e);
      }
    };
    fetchCollaborators();
  }, []);

  const handleShareClick = async () => {
    setIsShareMode(!isShareMode);
    setSelectedAssets([]);
    setSelectedCollaborator(null);
    setAlreadySharedAssetIds([]);
    
    // Fetch assets already shared by the logged-in user
    if (!isShareMode) {
      try {
        const response = await api.get('/sharing/assets/shared-by-me', {
          headers: getAuthHeaders()
        });
        setSharedByMeResponse(response.data);
      } catch (e) {
        console.error('Failed to fetch shared by me:', e);
        setSharedByMeResponse(null);
      }
    }
  };

  const isAssetAlreadyShared = (assetId) => {
    return alreadySharedAssetIds.includes(assetId);
  };

  const isAssetShareSelectionDisabled = (asset) => {
    if (!isShareMode) return false;
    if (asset?.isDraft) return true;
    return selectedCollaborator ? isAssetAlreadyShared(asset.id) : false;
  };

  const getShareableAssets = () =>
    filteredAssets.filter((asset) => !asset.isDraft && !isAssetAlreadyShared(asset.id));

  const handleAssetSelection = (assetId) => {
    const asset = filteredAssets.find((a) => a.id === assetId);
    if (!asset || isAssetShareSelectionDisabled(asset)) return;

    setSelectedAssets((prev) => {
      if (prev.includes(assetId)) {
        return prev.filter((id) => id !== assetId);
      } else {
        return [...prev, assetId];
      }
    });
  };

  const handleSelectAll = () => {
    const shareableAssets = getShareableAssets();

    if (selectedAssets.length === shareableAssets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(shareableAssets.map((asset) => asset.id));
    }
  };

  const handleCollaboratorSelect = async (collaboratorId) => {
    setSelectedCollaborator(collaboratorId);
    
    // Find ALL assets shared with the selected collaborator
    // The API returns one entry per shared asset, so we need to filter all matching entries
    if (sharedByMeResponse?.results && collaboratorId) {
      console.log('Looking for collaborator:', collaboratorId);
      console.log('Total shared entries:', sharedByMeResponse.results.length);
      
      // Filter ALL results that match the collaborator ID
      const matchingResults = sharedByMeResponse.results.filter(
        item => String(item.collaboratorId?.id) === String(collaboratorId)
      );
      
      console.log('Matching entries found:', matchingResults.length);
      
      // Extract asset IDs from all matching entries
      const assetIds = matchingResults
        .map(item => item.assetId?.id)
        .filter(Boolean);
      
      console.log('Already shared asset IDs:', assetIds);
      setAlreadySharedAssetIds(assetIds);
    } else {
      setAlreadySharedAssetIds([]);
    }
  };

  const handleShareAssets = async () => {
    if (!selectedCollaborator) {
      showErrorToast('Please select a collaborator.');
      return;
    }
    if (selectedAssets.length === 0) {
      showErrorToast('Please select at least one asset.');
      return;
    }

    const shareableAssetIds = selectedAssets.filter((assetId) => {
      const asset = filteredAssets.find((a) => a.id === assetId);
      return asset && !asset.isDraft;
    });

    if (shareableAssetIds.length === 0) {
      showErrorToast('Draft assets cannot be shared.');
      return;
    }

    try {
      // Example permissions, you can adjust as needed or make dynamic
      const permissions = {
        canViewDetails: true,
        canViewPhotos: true,
        canViewDocuments: false
      };
      const payload = {
        assetIds: shareableAssetIds,
        collaboratorId: selectedCollaborator || ' ', // Fallback to empty string if null
        permissions
      };
      const response = await api.post('/sharing/assets', payload, { headers: getAuthHeaders() });
      if (response) {
        setSharedAssetsCount(selectedAssets.length);
        setShowAssetsSharedModal(true);
        setIsShareMode(false);
        setSelectedAssets([]);
        setSelectedCollaborator(null);
      } else {
        showErrorToast('Failed to share assets.');
      }
    } catch (e) {
      let errorMsg = 'Failed to share assets.';
      if (e?.response?.data?.message) {
        errorMsg = e.response.data.message;
      } else if (e?.message) {
        errorMsg = e.message;
      }
      showErrorToast(errorMsg);
    }
  };

  // Removed API call on collaborator select

  const formatCurrency = (value, currency) => {
    if (!isValueVisible) {
      return "••••••";
    }
    if (typeof value === "number" && !isNaN(value)) {
      return formatLargeNumber(value, currency);
    }
    return "-";
  }

  const getPerformanceColor = (percentage) => {
    if (typeof percentage !== "number") return "text-muted";
    if (percentage > 10) return "text-success";
    if (percentage < 0) return "text-danger";
    return "text-warning";
  };

  const getStatusBadge = (status) => {
    switch ((status || "").toLowerCase()) {
      case "active":
        return "badge bg-success"
      case "inactive":
        return "badge bg-secondary"
      case "pending":
        return "badge bg-warning"
      default:
        return "badge bg-primary"
    }
  }

  const formatUpdatedAt = (updatedAt) => {
    if (!updatedAt) return "";
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffMs = now - updatedDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays >= 1) {
      return ` ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours >= 1) {
      return ` ${diffHours} hr ago`;
    } else {
      return ` ${diffMinutes} min ago`;
    }
  };

  const AddCollaboratorModal = ({ show, onClose, onSubmit }) => {
    // Get current user's data from Redux (must be called before any early returns)
    const userData = useSelector((state) => state.user?.data);
    
    // Extract owner's email - check top level and all identities
    const ownerEmails = [
      userData?.email,
      ...(userData?.identities || []).map(id => id.identifier).filter(id => id?.includes('@'))
    ].map(e => e?.toLowerCase()).filter(Boolean);
    
    // Extract owner's phone - check top level and identities array for SMS provider
    const smsIdentity = userData?.identities?.find(id => id.provider === 'sms');
    const ownerPhoneRaw = smsIdentity?.identifier || userData?.phone || userData?.mobileNumber || userData?.mobile || "NA";
    const ownerPhoneDigits = ownerPhoneRaw.replace(/\D/g, "");
    const defaultCountryCode = useMemo(
      () => getDialCodeFromUserProfile(userData),
      [userData]
    );

    if (!show) return null;

    const validationSchema = Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email")
        .required("Email is required")
        .test("not-owner-email", "You cannot add yourself as a collaborator", function(value) {
          if (!value || ownerEmails.length === 0) return true;
          const enteredEmail = value.toLowerCase();
          // Check if entered email matches any of owner's emails
          return !ownerEmails.includes(enteredEmail);
        }),
      countryCode: Yup.string().required("Country code is required"),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required")
        .test("not-owner-phone", "You cannot add yourself as a collaborator", function(value) {
          if (!value || !ownerPhoneDigits || ownerPhoneDigits === "NA" || ownerPhoneDigits.length < 10) return true;
          // Access countryCode from parent object (sibling field)
          const countryCode = this.parent?.countryCode || "";
          const countryCodeDigits = countryCode.replace(/\D/g, "");
          // Build entered phone number
          const enteredPhone = countryCodeDigits + value;
          // Check if entered phone matches owner's phone
          return !enteredPhone.endsWith(ownerPhoneDigits);
        }),
      notes: Yup.string(),
    });

    const initialValues = {
      firstName: "",
      lastName: "",
      email: "",
      countryCode: defaultCountryCode,
      mobile: "",
      notes: "",
    };

    return (
      <div className="share-modal-overlay" onClick={onClose}>
        <div className="share-collaborator-modal" onClick={(e) => e.stopPropagation()}>
          <button className="share-modal-close-btn" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>

          <div className="share-modal-header">
            <h5 className="share-modal-title">Who are you adding as your collaborator?</h5>
            <p className="share-modal-subtitle">
              It can be your spouse/partner, family member, or anyone you trust.
              you have full control over what you share with them.
            </p>
          </div>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={(formValues, formikHelpers) => {
              const payload = {
                email: formValues.email,
                name: `${formValues.firstName} ${formValues.lastName}`.trim(),
                phone: `${formValues.countryCode}${formValues.mobile}`.replace(/\s+/g, ''),
                permissions: {
                  canViewBasicInfo: true,
                  canViewDescription: true,
                  canViewPhotos: false,
                  canViewDocuments: false
                },
                notes: formValues.notes || ""
              };
              onSubmit(payload, formikHelpers);
            }}
          >
            {({ isSubmitting, values, errors, touched }) => (
              <Form className="share-collaborator-form">
                <div className="share-form-row">
                  <div className="share-form-group">
                    <label htmlFor="firstName">First Name*</label>
                    <Field
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-control"
                      placeholder="John"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="error-text"
                    />
                  </div>

                  <div className="share-form-group">
                    <label htmlFor="lastName">Last Name*</label>
                    <Field
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-control"
                      placeholder="Doe"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="error-text"
                    />
                  </div>
                </div>

                <div className="share-form-row">
                  <div className="share-form-group">
                    <label htmlFor="email">Email*</label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="john.doe@gmail.com"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="error-text"
                    />
                  </div>

                  <div className="share-form-group">
                    <label htmlFor="mobileNumber">Mobile Number*</label>
                    <div className="input-group">
                      <Field name="countryCode">
                        {({ field, form }) => (
                          <CountrySelectReact
                            value={field.value || ""}
                            onChange={val => form.setFieldValue("countryCode", val)}
                            maxWidth={120}
                            placeholder="Code"
                          />
                        )}
                      </Field>
                      <Field
                        type="text"
                        name="mobile"
                        className={`form-control ${errors.mobile && touched.mobile ? "is-invalid" : ""}`}
                        placeholder="Enter Mobile Number"
                        maxLength={10}
                      />
                    </div>
                    <ErrorMessage name="countryCode" component="div" className="error-text" />
                    <ErrorMessage name="mobile" component="div" className="error-text" />
                  </div>
                </div>

                <div className="share-form-group">
                  <label htmlFor="relation">Notes</label>
                  <Field
                    as="textarea"
                    id="notes"
                    name="notes"
                    className="form-control"
                  >
                  </Field>
                  <ErrorMessage
                    name="notes"
                    component="div"
                    className="error-text"
                  />
                </div>

                <div className="share-form-actions">
                  <button type="button" className="share-btn-cancel" onClick={onClose}>
                    <i className="bi bi-arrow-left"></i>
                    Cancel
                  </button>
                  <button type="submit" className="share-btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  };

  const SuccessModal = ({ show, onClose, title, message }) => {
    useEffect(() => {
      if (show) {
        const timer = setTimeout(() => {
          onClose();
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [show, onClose]);

    if (!show) return null;

    return (
      <div className="share-modal-overlay" onClick={onClose}>
        <div className="share-success-modal" onClick={(e) => e.stopPropagation()}>
          <button className="share-modal-close-btn" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>

          <div className="share-success-icon">
            <div className="share-success-circle">
              <i className="bi bi-check"></i>
            </div>
          </div>

          <h2 className="share-success-title">{title}</h2>
          <p className="share-success-message">{message}</p>

          <div className="share-success-actions">
            <button className="share-btn-back" onClick={onClose}>
              <i className="bi bi-arrow-left"></i>
              Back
            </button>
            <button className="share-btn-view-collaborators" onClick={() => { onClose(); navigate('/collaborators?tab=partial-access'); }}>
              View Collaborators
            </button>
          </div>

          <p className="share-redirect-text">Redirect you to profile in 5 sec.</p>
        </div>
      </div>
    );
  };
const totalResults = useSelector(
  (state) => state.assets.listMeta?.totalResults
);

  return (
    <Layout>
      <div className="container-fluid px-0">
        <div className="assets-liability-dashboard">
            <div className="row mb-2">
            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-shield-lock"></i>
                </div>
                    <div className="metric-content">
                  <div className="metric-label"><h6>Total Value</h6></div>
                  <div style={{display:'flex', alignItems:'center'}}>
                    <MetricValue text={`${getCurrencySymbol(performanceAnalytics?.currency)} ${formatCurrency(performanceAnalytics?.totalValue, performanceAnalytics?.currency)}`} />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card top-performer">
                <div className="metric-icon">
                  <i className="bi bi-trophy"></i>
                </div>
                    <div className="metric-content">
                  <div className="metric-label"><h6>Top Performer</h6></div>
                  <div style={{display:'flex', alignItems:'center'}}>
                    <MetricValue text={typeof performanceAnalytics?.topPerformer === 'object'
     ? (performanceAnalytics?.topPerformer?.name ?? 'N/A')
     : (performanceAnalytics?.topPerformer ?? 'N/A')} />
                  </div>
                </div>
                <div className="external-link-icon">
                  <i className="bi bi-arrow-up-right-circle"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-graph-down-arrow"></i>
                </div>
                  <div className="metric-content">
                  <div className="metric-label"><h6>Lowest Performer</h6></div>
                  <div style={{display:'flex', alignItems:'center'}}>
                    <MetricValue text={typeof performanceAnalytics?.worstPerformer === 'object'
     ? (performanceAnalytics?.worstPerformer?.name ?? 'N/A')
     : (performanceAnalytics?.worstPerformer ?? 'N/A')} />
                  </div>
                </div>
                <div className="external-link-icon">
                  <i className="bi bi-arrow-up-right-circle"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-pie-chart"></i>
                </div>
                  <div className="metric-content">
                  <div className="metric-label"><h6>Physical / Digital</h6></div>
                  <div style={{display:'flex', alignItems:'center'}}>
                    <MetricValue text={`${performanceAnalytics?.physicalAssetsCount} / ${performanceAnalytics?.digitalAssetsCount}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>

            <div className="row mb-4">
              <div className="col-12">
                <div className="filter-bar">
                <div className="filter-left">
                  <div className="items-filters">
                    <button
                      className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
                      onClick={() => setActiveFilter("all")}
                    >
                      All Assets {activeFilter === "all" && (
  <span className="filter-count">{totalResults || 0}</span>
)}

                    </button>
                    <button
                      className={`filter-btn ${activeFilter === "physical" ? "active" : ""}`}
                      onClick={() => setActiveFilter("physical")}
                    >
                      Physical Assets {activeFilter === "physical" && (
  <span className="filter-count">{totalResults || 0}</span>
)}
                    </button>
                    <button
                      className={`filter-btn ${activeFilter === "digital" ? "active" : ""}`}
                      onClick={() => setActiveFilter("digital")}
                    >
                      Digital Assets {activeFilter === "digital" && (
  <span className="filter-count">{totalResults || 0}</span>
)}
                    </button>
                    <button
                      className={`filter-btn ${activeFilter === "archived" ? "active" : ""}`}
                      onClick={() => {
                        setActiveFilter("archived");
                        fetchArchivedAssets();
                      }}
                    >
                      Archived
                      {activeFilter === "archived" && (
                        <span className="filter-count">{archivedAssets.length}</span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="filter-right">
                  <div className="search-container">
                    <i className="bi bi-search search-icon"></i>
                    <input
                      type="text"
                      className="form-control search-input"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* <button
                    className="action-btn"
                    onClick={() => setIsValueVisible(!isValueVisible)}
                    title={isValueVisible ? "Hide values" : "Show values"}
                  >
                    <i className={`bi ${isValueVisible ? "bi-eye" : "bi-eye-slash"}`}></i>
                  </button> */}

                  <button
                    className="action-btn"
                    onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                    title={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                  >
                    <i className={`bi ${viewMode === "grid" ? "bi-list-stars" : "bi-grid-3x3-gap"}`}></i>
                  </button>

                  <button
                    className={`action-btn share-btn ${isShareMode ? 'active' : ''}`}
                    onClick={handleShareClick}
                    title="Share Assets"
                  >
                    <i className="bi bi-share"></i>
                  </button>

                  <button className="action-btn export-btn" onClick={exportToExcel} title="Export All">
                    <i className="bi bi-download"></i>
                    Export All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isShareMode && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="share-collaborator-section">
                  <h5 className="share-collaborator-title">Select Collaborator</h5>
                  <div className="share-collaborator-selection-wrapper">
                    <Select
                      className="w-100"
                      classNamePrefix="react-select"
                      options={collaborators.map((collab) => ({
                        value: collab.id || collab._id || collab.collaboratorId,
                        label: `${collab.name || `${collab.firstName || ''} ${collab.lastName || ''}`.trim()} - ${collab.email || collab.phone || ''}`
                      }))}
                      value={collaborators
                        .filter(c => (c.id || c._id || c.collaboratorId) === selectedCollaborator)
                        .map(c => ({
                          value: c.id || c._id || c.collaboratorId,
                          label: `${c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()} - ${c.email || c.phone || ''}`
                        }))[0] || null}
                      onChange={(option) => handleCollaboratorSelect(option ? option.value : null)}
                      placeholder="Select Collaborator"
                      isClearable
                      styles={{
                        control:     (base) => ({ ...base, fontSize: '0.875rem' }),
                        placeholder: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                        option:      (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                        singleValue: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                      }}
                    />
                    <span className="share-select-divider">or</span>
                    <button
                      className="share-add-collaborator-btn"
                      onClick={() => setShowAddCollaboratorModal(true)}
                    >
                      + Add New Collaborator
                    </button>
                  </div>

                  <div className="share-select-all-wrapper">
                    <label className="share-select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedAssets.length === getShareableAssets().length && getShareableAssets().length > 0}
                        onChange={handleSelectAll}
                      />
                      <span>Select All Assets ({getShareableAssets().length})</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {categoryLoading && <Loader />}

          {/* Archived Assets Tab Content */}
          {activeFilter === "archived" && (
            <>
              {archivedLoading && <Loader />}
              {archivedError && (
                <div className="error-state">
                  <div className="alert alert-danger" role="alert">{archivedError}</div>
                </div>
              )}
              {!archivedLoading && !archivedError && archivedAssets.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon"><i className="bi bi-archive"></i></div>
                  <h5>No archived assets</h5>
                  <p>Assets that are archived due to plan downgrade will appear here.</p>
                </div>
              )}
              {!archivedLoading && archivedAssets.length > 0 && (
                <>
                  <div className="d-flex justify-content-end mb-4">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleBulkReactivate}
                      disabled={bulkReactivating || reactivateDisabled}
                      title={reactivateDisabled ? "Upgrade your plan to reactivate all assets" : "Reactivate all archived assets"}
                    >
                      {bulkReactivating ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Reactivating...</>
                      ) : (
                        <><i className="bi bi-arrow-counterclockwise"></i>Reactivate All ({archivedAssets.length})</>
                      )}
                    </button>
                  </div>
                  <div className="row">
                    {archivedAssets.map((asset) => {
                      const disabledReactivate = isReactivateDisabled(asset);
                      const assetId = asset.id || asset._id;
                      return (
                        <div key={assetId} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                          <div className="item-card archived-card">
                            {/* Vault/Archive Background Watermark */}
                            <div className="card-watermark">
                              <i className="bi bi-archive"></i>
                            </div>

                            <div className="item-card-body">
                              <div className="item-card-header">
                                <div className="item-actions-top">
                                  <button
                                    className="action-icon delete-btn"
                                    onClick={() => handleDeleteClick(assetId)}
                                    title="Delete Permanently"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                                <div className="item-card-title-row">
                                  <TruncatedTooltipText
                                    text={asset?.name || ""}
                                    className="item-card-title-value"
                                    Tag="h5"
                                    onlyWhenTruncated
                                  />
                                </div>
                              </div>
                              <div className="item-badges">
                                <span className="category-badge">
                                  <i className="bi bi-tag"></i>
                                  {formatAssetType(asset.assetType)}
                                </span>
                              </div>
                              <div className="item-stats">
                                <div className="stat-left">
                                  <div className="stat-item pill stat-archived">
                                    <i className="bi bi-archive-fill"></i>
                                    <span>{formatArchiveReason(asset.archiveDetails?.reason)}</span>
                                  </div>
                                </div>
                                <div className="stat-right">
                                  <div className="stat-item">
                                    <i className="bi bi-clock"></i>
                                    <span>Archived:{formatUpdatedAt(asset.archiveDetails?.archivedAt)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="item-card-footer">
                              {!disabledReactivate ? (
                                <button
                                  className="btn btn-outline-success btn-sm w-100"
                                  onClick={() => handleReactivate(assetId)}
                                >
                                  <i className="bi bi-arrow-counterclockwise"></i>
                                  Reactivate
                                </button>
                              ) : (
                                <div className="limit-alert-box w-100">
                                  <div className="limit-alert-text">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    Active limit reached. Archive another asset or upgrade.
                                  </div>
                                  <button
                                    className="limit-upgrade-link"
                                    onClick={() => navigate('/editprofile')}
                                  >
                                    Upgrade Plan <i className="bi bi-arrow-right-short"></i>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {activeFilter !== "archived" && (viewMode === "grid" ? (
            <div className="row">
              {filteredAssets.map((asset) => {
                const isAlreadyShared = isShareMode && selectedCollaborator && isAssetAlreadyShared(asset.id);
                const isShareSelectionDisabled = isAssetShareSelectionDisabled(asset);
                
                // Debug logging
                if (isShareMode && selectedCollaborator && alreadySharedAssetIds.length > 0) {
                  console.log('Asset:', asset.name, 'ID:', asset.id, 'Type:', typeof asset.id);
                  console.log('Already shared IDs:', alreadySharedAssetIds);
                  console.log('Match found:', alreadySharedAssetIds.includes(asset.id));
                }
                
                return (
                  <div key={asset.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                    <label 
                      htmlFor={asset.id} 
                      className={`item-card ${isShareMode && selectedAssets.includes(asset.id) ? 'share-selected' : ''} ${isAlreadyShared ? 'share-disabled' : ''}`}
                      style={isAlreadyShared ? { opacity: 0.5 } : {}}
                    >
                    <div className="item-card-body">
                      <div className="item-card-header">
                        <div className="item-actions-top">
                          <button
                            className="action-icon duplicate-btn"
                            onClick={() => duplicateAsset(asset.id)}
                            title="Duplicate"
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className="bi bi-files"></i>
                          </button>
                          <button
                            className="action-icon"
                            onClick={() => handleArchive(asset.id)}
                            title="Archive"
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className="bi bi-archive"></i>
                          </button>
                          <button
                            className="action-icon delete-btn"
                            onClick={() => handleDeleteClick(asset.id)}
                            title="Delete"
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                          <button
                            className={`action-icon favorite-btn ${asset.isFavorite ? "active" : ""}`}
                            onClick={() => toggleFavorite(asset.id)}
                            title={asset?.isFavorite ? "Remove from favorites" : "Add to favorites"}
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className={`bi ${asset?.isFavorite ? "bi-star-fill" : "bi-star"}`}></i>
                          </button>
                        </div>
                        <div className="item-card-title-row">
                          {isShareMode && (
                            <div className={`share-asset-checkbox-wrapper${asset.isDraft ? ' share-draft-disabled' : ''}`}>
                              <input
                                type="checkbox"
                                id={asset.id}
                                checked={selectedAssets.includes(asset.id)}
                                onChange={() => handleAssetSelection(asset.id)}
                                className="share-asset-checkbox"
                                disabled={isShareSelectionDisabled}
                              />
                            </div>
                          )}
                          <TruncatedTooltipText
                            text={assetCardTitle(asset)}
                            className="item-card-title-value"
                            Tag="h5"
                            onlyWhenTruncated
                          />
                        </div>
                      </div>

                      <div className="item-badges">
                        <span className={`item-type-badge ${(asset?.assetCategory || "").toLowerCase()}`}>{asset.assetCategory}</span>
                        <span className="category-badge">
                          <i className="bi bi-tag"></i>
                          {formatAssetType(asset.assetType)}
                        </span>
                        {asset.isDraft && (
                          <span className="draft-badge">
                            <i className="bi bi-pencil"></i>
                            Draft
                          </span>
                        )}
                      </div>

                      <div className="item-value"><h4>{getCurrencySymbol(asset?.purchaseCurrency)} {formatCurrency(asset?.currentValue, asset?.purchaseCurrency)}</h4></div>
                      <div>{isAlreadyShared && <span className="already-shared-badge">Shared</span>}</div> 

                      <div className="item-performance">
                        <i className="bi bi-graph-up"></i>
                        <span className="performance-title">Performance: </span>
                        <span className="performance-value">
                          {typeof asset?.performance?.percentage === "number"
                            ? `${asset?.performance?.percentage}%`
                            : "-"}
                          {typeof asset?.performance?.percentage === "number" && asset?.performance?.percentage > 0 && (
                            <i className="bi bi-arrow-up"></i>
                          )}
                          {typeof asset?.performance?.percentage === "number" && asset?.performance?.percentage < 0 && (
                            <i className="bi bi-arrow-down"></i>
                          )}
                        </span>
                      </div>
                      <div className="item-stats">
                        <div className="stat-left">
                          <div className={`stat-item pill stat-status ${asset?.isActive ? "active" : ""}`}>
                            <i className="bi bi-circle-fill"></i>
                            <span>{asset?.isActive ? "Active" : "Inactive"}</span>
                          </div>
                          <div className="stat-item pill stat-attachments">
                            <i className="bi bi-paperclip"></i>
                            <span>{((asset?.photos?.length || 0) + (asset?.documents?.length || 0))} Attachments</span>
                          </div>
                        </div>
                        <div className="stat-right">
                          <div className="stat-item">
                            <i className="bi bi-clock"></i>
                            <span>Updated: {formatUpdatedAt(asset?.updatedAt)} </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Edit and View buttons in footer - disabled when already shared */}
                    <div className="item-card-footer" style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                      <button 
                        className="btn btn-outline-primary btn-sm" 
                        disabled={isAlreadyShared}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/my-assets/${asset?.id}`, { state: { edit: true } });
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                        {asset.isDraft ? "Edit Draft" : "Edit"}
                      </button>
                      <button 
                        className="btn btn-primary btn-sm" 
                        disabled={isAlreadyShared}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/my-assets/${asset?.id}`);
                        }}
                      >
                        View
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </div>
                  </label>
                </div>
              );
              })}
            </div>
          ) : (
            <div className="items-table-container">
              <div className="table-responsive">
                <table className="table items-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Value</th>
                      <th>Performance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((asset) => {
                      const isAlreadyShared = isShareMode && selectedCollaborator && isAssetAlreadyShared(asset.id);
                      const isShareSelectionDisabled = isAssetShareSelectionDisabled(asset);
                      return (
                        <tr 
                          key={asset.id} 
                          className={`${isShareMode && selectedAssets.includes(asset.id) ? 'share-selected' : ''} ${isAlreadyShared ? 'share-disabled' : ''}`}
                          style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          onClick={(e) => {
                            const tag = e.target.tagName.toLowerCase();
                            if (tag === "button" || tag === "input" || e.target.closest(".table-actions")) return;

                            if (isShareMode && !isShareSelectionDisabled) {
                              handleAssetSelection(asset.id);
                            }
                          }}
                        >
                          <td>
                            <div className="item-name-cell">
                              {isShareMode ? (
                                <>
                                  <input
                                    type="checkbox"
                                    checked={selectedAssets.includes(asset.id)}
                                    onChange={() => handleAssetSelection(asset.id)}
                                    className={`share-asset-checkbox-list${asset.isDraft ? ' share-draft-disabled' : ''}`}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isShareSelectionDisabled}
                                  />
                                  {isAlreadyShared && <span className="already-shared-badge">Already shared</span>}
                                </>
                              ) : (
                              <button
                                className={`favorite-btn-small ${asset.isFavorite ? "active" : ""}`}
                                onClick={() => toggleFavorite(asset.id)}
                              >
                                <i className={`bi ${asset.isFavorite ? "bi-star-fill" : "bi-star"}`}></i>
                              </button>
                            )}
                            <div className="item-info">
                              <div className="name">{asset?.name}</div>
                              <div className="meta">
                                {asset.isDraft && <span className="draft-text">Draft</span>}
                                <span>{formatUpdatedAt(asset?.updatedAt)} Updated</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge item-type-badge ${(asset.assetCategory || "").toLowerCase()}`}>{asset.assetCategory}</span>
                        </td>
                        <td style={{ textTransform: "capitalize" }}>{asset?.assetType}</td>
                        <td className="value-cell">{getCurrencySymbol(asset?.purchaseCurrency)} {formatCurrency(asset?.currentValue, asset?.purchaseCurrency)}</td>
                        <td>
                          <span className={getPerformanceColor(asset.performance?.percentage)}>
                            {typeof asset.performance?.percentage === "number"
                              ? `${asset.performance.percentage}%`
                              : "-"}
                            {typeof asset.performance?.percentage === "number" && asset?.performance?.percentage > 0 && (
                              <i className="bi bi-arrow-up"></i>
                            )}
                            {typeof asset.performance?.percentage === "number" && asset?.performance?.percentage < 0 && (
                              <i className="bi bi-arrow-down"></i>
                            )}
                          </span>
                        </td>
                        <td>
                          <span className={getStatusBadge(asset?.status)}>{asset?.status}</span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="action-icon duplicate-btn"
                              onClick={() => duplicateAsset(asset?.id)}
                              title="Duplicate"
                            >
                              <i className="bi bi-files"></i>
                            </button>
                            <button
                              className="action-icon"
                              onClick={() => handleArchive(asset?.id)}
                              title="Archive"
                            >
                              <i className="bi bi-archive"></i>
                            </button>
                            <button
                              className="action-icon delete-btn"
                              onClick={() => handleDeleteClick(asset?.id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                            <button className="action-icon edit-btn" title="Edit"  onClick={() =>
    navigate(`/my-assets/${asset?.id}`, {
      state: { edit: true }
    })
  }>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="action-icon view-btn" title="View" onClick={() => navigate(`/my-assets/${asset.id}`)}>
                              <i className="bi bi-arrow-right"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {loading && <Loader/>}

          {error && (
            <div className="error-state">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          )}

          {activeFilter !== "archived" && !loading && filteredAssets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="bi bi-inbox"></i>
              </div>
              <h5>No assets found</h5>
              {/* Only suggest changing the search when one is active; a new user just has nothing yet (ML-21) */}
              <p>{(searchTerm && searchTerm.trim()) || activeFilter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Add your first asset with the + button to start tracking it."}</p>
            </div>
          ):(
             <div className="pagination-controls pagination-sticky" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            <button
              className="btn-sm pagination-text"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pg) => (
              <button
                key={pg}
                className={`btn btn-sm paginations-list ${pg === page ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => handlePageChange(pg)}
                disabled={pg === page}
              >
                {pg}
              </button>
            ))}
            <button
              className="btn-sm pagination-text"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
            <span style={{ alignSelf: 'center', fontWeight: 500, marginLeft: 12 }}>
              of {totalPages} pages
            </span>
          </div>
          )}

          {isShareMode && selectedAssets.length > 0 && (
            <div className="share-actions-bar">
              <button className="share-btn-cancel-share" onClick={handleShareClick}>
                <i className="bi bi-arrow-left"></i>
                Cancel
              </button>
              <button className="share-btn-share-now" onClick={handleShareAssets}>
                Share Now
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          )}
        </div>

        <AddCollaboratorModal
          show={showAddCollaboratorModal}
          onClose={() => setShowAddCollaboratorModal(false)}
          onSubmit={async (payload, formikHelpers) => {
    try {
      const response = await api.post('/sharing/collaborators', payload, { headers: getAuthHeaders() });
      if (response && response.data) {
        // Fetch updated list after successful add
        const listRes = await api.get('/sharing/collaborators?status=active&limit=20&page=1', { headers: getAuthHeaders() });
        let collabList = Array.isArray(listRes.data?.results)
          ? listRes.data.results.map(item => ({
              id: item.collaboratorId?.id,
              name: item.collaboratorId?.name || "",
              email: item.collaboratorId?.email || "",
              mobileNumber: item.collaboratorId?.mobileNumber || "",
              firstName: item.collaboratorId?.firstName || "",
              lastName: item.collaboratorId?.lastName || ""
            }))
          : [];
        setCollaborators(collabList);
        setNewCollaboratorName(payload.name);
        setShowAddCollaboratorModal(false);
        setShowCollaboratorAddedModal(true);
        formikHelpers.resetForm();
      }
    } catch (e) {
      // Handle duplicate collaborator error (409) specially
      if (e?.response?.status === 409) {
        showErrorToast('This collaborator has already been added.');
      } else {
        showErrorToast(e?.message || 'Failed to add collaborator.');
      }
    } finally {
      formikHelpers.setSubmitting(false);
    }
  }}
        />

        <SuccessModal
          show={showCollaboratorAddedModal}
          onClose={() => setShowCollaboratorAddedModal(false)}
          title="Collaborator Added Successfully!"
          message={`we have added ${newCollaboratorName} as Collaborator with full access of your Profile. Now he can access all your assets and liability`}
        />

        <SuccessModal
          show={showAssetsSharedModal}
          onClose={() => setShowAssetsSharedModal(false)}
          title={`${sharedAssetsCount} Assets Shared Successfully!`}
          message={`we have share assets to ${collaborators.find(c => c.id === selectedCollaborator)?.firstName || 'Collaborator'} ${collaborators.find(c => c.id === selectedCollaborator)?.lastName || ''} as Collaborator with partial access of your assets. Now he can access all your assets and liability.`}
        />

        <ConfirmModal
          show={deleteModal.show}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          loading={deleteLoading}
          title={"Delete Asset"}
          message={"Are you sure you want to delete this asset?"}
        />
      </div>
    </Layout>
  )
}

export default MyAssets